import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Cookie, Settings, Check, X, Shield, BarChart3 } from "lucide-react";
import {
  getConsent,
  saveConsent,
  initAnalytics,
  disableAnalytics,
} from "@/lib/analytics";

/**
 * DSGVO/TTDSG-konformer Cookie-Consent-Banner.
 * - Fixed bottom, elegantes Blau/Gold-Design
 * - Kategorien: notwendig (immer aktiv), Analyse (opt-in)
 * - Speichert Auswahl in localStorage; PostHog wird nur nach Zustimmung geladen
 */
export const CookieConsent = () => {
  const [open, setOpen] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [analyticsChecked, setAnalyticsChecked] = useState(false);

  useEffect(() => {
    const stored = getConsent();
    if (!stored) {
      // Delay banner so it doesn't compete with hero content
      const t = setTimeout(() => setOpen(true), 600);
      return () => clearTimeout(t);
    }
  }, []);

  const handleAcceptAll = useCallback(() => {
    saveConsent({ analytics: true });
    initAnalytics();
    setOpen(false);
  }, []);

  const handleRejectAll = useCallback(() => {
    saveConsent({ analytics: false });
    disableAnalytics();
    setOpen(false);
  }, []);

  const handleSaveSettings = useCallback(() => {
    saveConsent({ analytics: analyticsChecked });
    if (analyticsChecked) initAnalytics();
    else disableAnalytics();
    setOpen(false);
  }, [analyticsChecked]);

  // Expose global helper so users can reopen from Datenschutzerklärung
  useEffect(() => {
    window.__ssOpenCookieSettings = () => {
      const stored = getConsent();
      setAnalyticsChecked(stored?.analytics ?? false);
      setShowSettings(true);
      setOpen(true);
    };
    return () => {
      delete window.__ssOpenCookieSettings;
    };
  }, []);

  if (!open) return null;

  return (
    <>
      {/* Subtle dim overlay, does not block interaction on desktop */}
      <div
        className="fixed inset-0 bg-black/25 backdrop-blur-[2px] z-[80] pointer-events-none"
        aria-hidden="true"
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-label="Cookie-Einstellungen"
        data-testid="cookie-consent-banner"
        className="fixed inset-x-0 bottom-0 z-[90] p-3 sm:p-5"
      >
        <div className="ss-container">
          <div className="relative rounded-2xl bg-white border border-slate-200 shadow-[0_25px_80px_rgba(2,6,23,0.35)] overflow-hidden">
            {/* Gold accent bar */}
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[var(--ss-gold)] via-[#F5C542] to-[var(--ss-gold-600)]" />

            <div className="p-5 sm:p-7 lg:p-8">
              <div className="flex flex-col lg:flex-row lg:items-start gap-5 lg:gap-8">
                {/* Left: text */}
                <div className="flex items-start gap-3 lg:flex-1">
                  <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[var(--ss-gold)]/15 text-[#EAB308] border border-[var(--ss-gold)]/30">
                    <Cookie className="h-5 w-5" strokeWidth={2.2} />
                  </span>
                  <div>
                    <h2 className="font-display font-semibold text-slate-900 text-lg sm:text-xl">
                      Wir respektieren Ihre Privatsphäre
                    </h2>
                    <p className="mt-2 text-sm text-slate-600 leading-relaxed max-w-2xl">
                      Wir verwenden Cookies und ähnliche Technologien. Manche sind{" "}
                      <strong className="text-slate-900">technisch notwendig</strong>, andere helfen uns bei der{" "}
                      <strong className="text-slate-900">pseudonymisierten Reichweitenanalyse</strong>, um unsere Webseite zu verbessern. Sie können Ihre Auswahl jederzeit anpassen.
                    </p>
                    <p className="mt-2 text-xs text-slate-500">
                      Details in unserer{" "}
                      <Link
                        to="/datenschutz"
                        className="text-[#0A2340] font-semibold underline underline-offset-4 hover:text-[#EAB308]"
                        onClick={() => setOpen(false)}
                        data-testid="cookie-link-datenschutz"
                      >
                        Datenschutzerklärung
                      </Link>{" "}
                      und im{" "}
                      <Link
                        to="/impressum"
                        className="text-[#0A2340] font-semibold underline underline-offset-4 hover:text-[#EAB308]"
                        onClick={() => setOpen(false)}
                        data-testid="cookie-link-impressum"
                      >
                        Impressum
                      </Link>
                      .
                    </p>
                  </div>
                </div>

                {/* Right: actions */}
                <div className="flex flex-col gap-2.5 lg:w-72 shrink-0">
                  <button
                    type="button"
                    onClick={handleAcceptAll}
                    className="ss-btn-primary w-full justify-center"
                    data-testid="cookie-accept-all"
                  >
                    <Check className="h-4 w-4" strokeWidth={2.5} />
                    Alle akzeptieren
                  </button>
                  <button
                    type="button"
                    onClick={handleRejectAll}
                    className="inline-flex items-center justify-center gap-2 rounded-xl px-5 py-3 text-sm font-semibold border border-slate-300 text-slate-800 hover:bg-slate-50 transition"
                    data-testid="cookie-reject-all"
                  >
                    <X className="h-4 w-4" />
                    Nur notwendige
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const stored = getConsent();
                      setAnalyticsChecked(stored?.analytics ?? false);
                      setShowSettings((v) => !v);
                    }}
                    className="inline-flex items-center justify-center gap-2 rounded-xl px-5 py-2.5 text-xs font-semibold text-slate-600 hover:text-slate-900 transition"
                    data-testid="cookie-toggle-settings"
                    aria-expanded={showSettings}
                  >
                    <Settings className="h-3.5 w-3.5" />
                    {showSettings ? "Einstellungen ausblenden" : "Individuelle Einstellungen"}
                  </button>
                </div>
              </div>

              {/* Settings panel */}
              {showSettings && (
                <div
                  className="mt-6 pt-6 border-t border-slate-200 grid sm:grid-cols-2 gap-4"
                  data-testid="cookie-settings-panel"
                >
                  {/* Necessary */}
                  <div className="rounded-xl border border-slate-200 bg-slate-50/50 p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-3">
                        <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-200 text-slate-700">
                          <Shield className="h-4 w-4" />
                        </span>
                        <div>
                          <div className="font-semibold text-slate-900 text-sm">Notwendig</div>
                          <div className="text-xs text-slate-600 mt-1 leading-relaxed">
                            Erforderlich für den Betrieb der Webseite. Können nicht deaktiviert werden.
                          </div>
                        </div>
                      </div>
                      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 px-2.5 py-1 text-[11px] font-semibold whitespace-nowrap">
                        <Check className="h-3 w-3" /> Aktiv
                      </span>
                    </div>
                  </div>

                  {/* Analytics */}
                  <label
                    className="rounded-xl border border-slate-200 bg-white p-4 cursor-pointer hover:border-[var(--ss-gold)]/50 hover:bg-slate-50/50 transition-colors"
                    data-testid="cookie-toggle-analytics"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-3">
                        <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[var(--ss-gold)]/15 text-[#EAB308] border border-[var(--ss-gold)]/25">
                          <BarChart3 className="h-4 w-4" />
                        </span>
                        <div>
                          <div className="font-semibold text-slate-900 text-sm">Reichweitenanalyse</div>
                          <div className="text-xs text-slate-600 mt-1 leading-relaxed">
                            Pseudonymisierte Nutzungsstatistiken (PostHog) zur Verbesserung der Webseite.
                          </div>
                        </div>
                      </div>
                      <input
                        type="checkbox"
                        checked={analyticsChecked}
                        onChange={(e) => setAnalyticsChecked(e.target.checked)}
                        className="mt-1 h-5 w-5 rounded border-slate-300 text-[#EAB308] focus:ring-2 focus:ring-[var(--ss-gold)]/40 accent-[#EAB308]"
                        aria-label="Reichweitenanalyse aktivieren"
                      />
                    </div>
                  </label>

                  <div className="sm:col-span-2 flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-2 pt-1">
                    <button
                      type="button"
                      onClick={handleSaveSettings}
                      className="ss-btn-primary justify-center"
                      data-testid="cookie-save-settings"
                    >
                      Auswahl speichern
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
